import { queryOptions } from "@tanstack/react-query";

import { ProjectHeaderName } from "@/api/ApiClientProvider";
import { useApi } from "@/api/hooks/useApi";
import type { ILoqCredentialsDto, IntegrationReconciliationDataTypeDto } from "@/api/types";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";

export const useIntegrationQueries = () => {
  const api = useApi();
  const projectId = useProjectId();
  const requestParams = (projectIdOverride?: string) =>
    projectIdOverride ? { headers: { [ProjectHeaderName]: projectIdOverride } } : undefined;

  return {
    remsConfiguration: (projectIdOverride?: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_REMS_CONFIGURATION(projectIdOverride ?? projectId),
        queryFn: () => api.getIntegrationsRemsV1(requestParams(projectIdOverride)),
        select: commonAPIDataSelector,
      }),

    remsReconciliationData: (dataType: IntegrationReconciliationDataTypeDto) =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_REMS_RECONCILIATION_DATA(projectId, dataType),
        queryFn: () => api.getIntegrationsRemsReconciliationDetailsV1(dataType),
        select: commonAPIDataSelector,
      }),

    remsReconciliationRuns: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_REMS_RECONCILIATION_RUNS(projectId),
        queryFn: () => api.getIntegrationsRemsReconciliationRunsV1(),
        select: commonAPIDataSelector,
      }),

    homeDnaConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_HOMEDNA_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsHomednaV1(),
        select: commonAPIDataSelector,
      }),

    iqbiConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_IQBI_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsIqbiV1(),
        select: commonAPIDataSelector,
      }),

    dicoConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_DICO_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsDicoV1(),
        select: commonAPIDataSelector,
      }),

    healthDistrictConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_HEALTH_DISTRICT_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsHealthdistrictV1(),
        select: commonAPIDataSelector,
      }),

    ziezodanConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_ZIEZODAN_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsZiezodanV1(),
        select: commonAPIDataSelector,
      }),

    outlookConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_OUTLOOK_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsOutlookV1(),
        select: commonAPIDataSelector,
      }),

    jimConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_JIM_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsJimV1(),
        select: commonAPIDataSelector,
      }),

    jimRooms: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_JIM_ROOMS(projectId),
        queryFn: () => api.getIntegrationsJimRoomsV1(),
        select: commonAPIDataSelector,
        retry: false,
      }),

    myPupConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_MY_PUP_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsMypupV1(),
        select: commonAPIDataSelector,
      }),

    bringmeConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_BRINGME_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsBringmeV1(),
        select: commonAPIDataSelector,
      }),

    copilotConfigurations: (projectIdOverride?: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_COPILOT_CONFIGURATION(projectIdOverride ?? projectId),
        queryFn: () => api.getIntegrationsCopilotPromptConfigurationsV1(requestParams(projectIdOverride)),
        select: commonAPIDataSelector,
      }),

    swoogoConfigurations: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_SWOOGO_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsSwoogoV1(),
        select: commonAPIDataSelector,
      }),

    officeRndConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_OFFICE_RND_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsOfficerndV1(),
        select: commonAPIDataSelector,
      }),

    hubSpotConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_HUBSPOT_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsHubspotV1(),
        select: commonAPIDataSelector,
      }),

    wunderbricksConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_WUNDERBRICKS_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsWunderbricksV1(),
        select: commonAPIDataSelector,
      }),
    wunderbricksTicketStatusOptions: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_WUNDERBRICKS_TICKET_STATUS_OPTIONS(projectId),
        queryFn: () => api.getIntegrationsWunderbricksTicketStatusOptionsV1(),
        select: commonAPIDataSelector,
      }),

    iloqConfiguration: (projectIdOverride?: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_ILOQ_CONFIGURATION(projectIdOverride ?? projectId),
        queryFn: () => api.getIntegrationsIloqV1(requestParams(projectIdOverride)),
        select: commonAPIDataSelector,
      }),

    iloqLockGroups: (credentials: ILoqCredentialsDto, projectIdOverride?: string) =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_ILOQ_LOCK_GROUPS(
          projectIdOverride ?? projectId,
          credentials.serviceAddress,
          credentials.customerCode,
          credentials.username,
        ),
        queryFn: () => api.postIntegrationsIloqLockGroupsV1(credentials, requestParams(projectIdOverride)),
        retry: false,
      }),

    iloqOptions: (
      lockGroupId: string,
      credentials: ILoqCredentialsDto,
      connectionRevision: number,
      projectIdOverride?: string,
    ) =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_ILOQ_OPTIONS(
          projectIdOverride ?? projectId,
          lockGroupId,
          credentials.serviceAddress,
          credentials.customerCode,
          credentials.username,
          connectionRevision,
        ),
        queryFn: () => api.postIntegrationsIloqOptionsV1(lockGroupId, credentials, requestParams(projectIdOverride)),
        retry: false,
      }),

    jansenHuybregtsConfiguration: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_JANSEN_HUYBREGTS_CONFIGURATION(projectId),
        queryFn: () => api.getIntegrationsJansenHuybregtsV1(),
        select: commonAPIDataSelector,
      }),

    jansenHuybregtsTicketStatusOptions: () =>
      queryOptions({
        queryKey: QUERY_KEYS.INTEGRATION_JANSEN_HUYBREGTS_TICKET_STATUS_OPTIONS(projectId),
        queryFn: () => api.getIntegrationsJansenHuybregtsTicketStatusOptionsV1(),
        select: commonAPIDataSelector,
      }),
  };
};
